import { useQuery } from '@tanstack/react-query';
import { Hourglass } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PendingValue } from '@/components/surface/ui';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useSubscriptionAccess } from '@/hooks/use-subscription-access';
import { AUTOMATION_CATALOG_KEY, automationApi } from '@/lib/automation-api';
import { countOf, daysLeftPhrase, trialEndsPhrase, usageLevel, type UsageLevel } from '@/lib/trial';
import { cn } from '@/lib/utils';

/** A limited allowance as the page that owns it reports it. Undefined while that page's query loads. */
export interface Allowance {
  used: number;
  /** Negative for no ceiling, which a trial never has but an operator could configure. */
  limit: number;
}

interface Meter {
  key: string;
  label: string;
  to: string;
  allowance: Allowance | undefined;
  /** The noun, singular and plural, for the sentence said when this one runs out. */
  one: string;
  many: string;
}

/**
 * The free trial on the dashboard: how long is left, and how much of each allowance is used.
 *
 * ## Numbers from the pages that enforce them
 *
 * Each meter reads the quota the Servers, Monitoring and Command Runner endpoints report — the same
 * figures those endpoints check before creating anything — rather than the trial plan's feature list.
 * A per-account override an operator set is then already in the number, and the meter cannot say "one
 * left" while the API refuses the next one.
 *
 * ## No sales pitch while there is nothing to decide
 *
 * The button to choose a plan appears when there is a reason to press it: two days or less left, or an
 * allowance used up or nearly so. Before that the panel is a clock and three meters. A trial that asks
 * for money on its first morning is a trial somebody closes.
 */
export function TrialPanel({ servers, monitors }: { servers: Allowance | undefined; monitors: Allowance | undefined }) {
  const { trial } = useSubscriptionAccess();

  const catalog = useQuery({
    queryKey: AUTOMATION_CATALOG_KEY,
    queryFn: () => automationApi.catalog(),
    staleTime: 60_000,
    enabled: trial?.status === 'running',
  });

  if (trial === null || trial.status !== 'running' || trial.days_left === null || trial.ends_at === null) return null;

  const runs = catalog.data?.command_runs;

  const meters: Meter[] = [
    { key: 'servers', label: 'Servers', to: '/servers', allowance: servers, one: 'server', many: 'servers' },
    { key: 'monitors', label: 'Monitors', to: '/monitoring', allowance: monitors, one: 'monitor', many: 'monitors' },
    {
      key: 'command_runs',
      label: 'Command Runner runs',
      to: '/automation/commands',
      // Null from the API means the account has no ceiling on commands, which leaves nothing to meter.
      allowance: runs === undefined ? undefined : runs === null ? { used: 0, limit: -1 } : { used: runs.used, limit: runs.limit },
      one: 'Command Runner run',
      many: 'Command Runner runs',
    },
  ];

  const levels = meters.map((meter) =>
    meter.allowance === undefined ? 'ok' : usageLevel(meter.allowance.used, meter.allowance.limit),
  );

  const reasons: string[] = [];

  if (trial.days_left <= 2) {
    reasons.push('Choose a plan before the trial ends so monitor checks and scheduled backups carry on without a gap.');
  }

  meters.forEach((meter, index) => {
    const allowance = meter.allowance;

    if (allowance === undefined) return;

    if (levels[index] === 'reached') {
      reasons.push(
        meter.key === 'command_runs'
          ? `All ${countOf(allowance.limit, meter.one, meter.many)} in the trial are used. Playbooks and templates still work.`
          : `The trial's ${countOf(allowance.limit, meter.one, meter.many)} ${allowance.limit === 1 ? 'is' : 'are'} in use.`,
      );
    } else if (levels[index] === 'near') {
      reasons.push(`${countOf(allowance.limit - allowance.used, meter.one, meter.many)} left in the trial.`);
    }
  });

  const urgent = trial.days_left <= 2;

  return (
    <Card className="p-5">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center">
        <div className="flex min-w-0 shrink-0 items-start gap-3 lg:w-60">
          <span
            className={cn(
              'flex size-9 shrink-0 items-center justify-center rounded-lg',
              urgent ? 'bg-warning/10 text-warning' : 'bg-primary/10 text-primary',
            )}
          >
            <Hourglass className="size-[18px]" aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground">Free Trial</p>
            <p className={cn('mt-1 text-2xl font-semibold tabular-nums tracking-tight', urgent && 'text-warning')}>
              {daysLeftPhrase(trial.days_left)}
            </p>
            <p className="mt-1 text-xs text-muted-foreground" title={new Date(trial.ends_at).toLocaleString()}>
              {trialEndsPhrase(trial.ends_at)}
            </p>
          </div>
        </div>

        <ul className="grid min-w-0 flex-1 grid-cols-1 gap-3 sm:grid-cols-3">
          {meters.map((meter, index) => (
            <li key={meter.key} className="min-w-0">
              <MeterRow meter={meter} level={levels[index] ?? 'ok'} />
            </li>
          ))}
        </ul>

        {reasons.length > 0 && (
          <div className="flex shrink-0 flex-col gap-3 lg:w-72 lg:items-end lg:text-right">
            <p className="text-[13px] text-muted-foreground">{reasons.slice(0, 2).join(' ')}</p>
            <Button asChild size="sm">
              <Link to="/billing">Choose a plan</Link>
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

/** One allowance: how many are used of how many, and a bar that fills as they are. */
function MeterRow({ meter, level }: { meter: Meter; level: UsageLevel }) {
  const allowance = meter.allowance;
  const unlimited = allowance !== undefined && allowance.limit < 0;
  const share = allowance === undefined || unlimited || allowance.limit === 0 ? 0 : Math.min(1, allowance.used / allowance.limit);

  return (
    <Link to={meter.to} className="block rounded-md bg-muted/30 px-3 py-2 transition-colors hover:bg-muted/50">
      <span className="block truncate text-xs text-muted-foreground">{meter.label}</span>
      <span className="mt-0.5 flex items-baseline justify-between gap-2">
        {/* "1 / 3", the same used-over-limit figure as the dashboard's tiles. */}
        <span className="text-lg font-semibold tabular-nums">
          {allowance === undefined ? <PendingValue /> : allowance.used}
          {allowance !== undefined && !unlimited && (
            <span className="text-sm font-normal text-muted-foreground"> / {allowance.limit}</span>
          )}
        </span>
        <span className={cn('truncate text-xs', level === 'ok' ? 'text-muted-foreground' : 'text-warning')}>
          {allowance === undefined
            ? ''
            : unlimited
              ? 'No limit'
              : level === 'reached'
                ? 'All in use'
                : `${allowance.limit - allowance.used} left`}
        </span>
      </span>
      {/* Decorative: the figures above say the same thing in words. */}
      <span aria-hidden className="mt-2 block h-1.5 overflow-hidden rounded-full bg-muted">
        <span
          className={cn('block h-full rounded-full', level === 'ok' ? 'bg-primary' : 'bg-warning')}
          style={{ width: `${Math.round(share * 100)}%` }}
        />
      </span>
    </Link>
  );
}
